import React, { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  getIdToken,
  sendPasswordResetEmail,
} from "firebase/auth";
import { GoogleAuthProvider } from "firebase/auth";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { auth, gprovider, logAnalyticsEvent } from "../firebase";
import { jwtDecode } from "jwt-decode";
import "./LoginForm.css";
import { FcGoogle } from "react-icons/fc";
import configData from "../configure";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { safeSetItem, safeRemoveItem } from "../utils/storageUtils";
import { recordUserSession } from "../utils/presenceService";

const LoginForm = () => {
  /* ---------- Core UI state ---------- */
  const [errorMsg, setErrorMsg] = useState("");
  const [logoutMsg, setLogoutMsg] = useState("");
  const [isSignupMode, setIsSignupMode] = useState(false);
  const { setToken } = useAuth();

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");          // <-- independent email for reset
  const [showReset, setShowReset] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetError, setResetError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  /* ---------- Token expiry handling ---------- */
  useEffect(() => {
    checkTokenExpiry();
  }, []);

  const checkTokenExpiry = () => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) return;

    try {
      const decoded = jwtDecode(storedToken);
      const expiry = decoded.exp * 1000;
      const now = Date.now();

      if (now >= expiry) {
        performLogout("Your session has expired. Please log in again.");
      } else {
        setToken(storedToken);
        scheduleLogout(expiry);
      }
    } catch {
      performLogout("Invalid session. Please log in again.");
    }
  };

  const performLogout = (msg) => {
    auth.signOut();
    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpiry");
    setToken(null);
    setLogoutMsg(msg);
  };

  const scheduleLogout = (expiry) => {
    if (window.logoutTimer) clearTimeout(window.logoutTimer);
    const delay = Math.min(expiry - Date.now(), 2_147_483_647);
    if (delay > 0) {
      window.logoutTimer = setTimeout(() => {
        performLogout("Session expired. You have been logged out.");
      }, delay);
    }
  };

  /* ---------- Form handlers ---------- */
  const changeLogin = (e) =>
    setLoginData({ ...loginData, [e.target.name]: e.target.value });

  const validateSignup = () => {
    if (!loginData.email || !loginData.password) {
      setErrorMsg("Email and password are required");
      return false;
    }
    if (loginData.password.length < 6) {
      setErrorMsg("Password must be at least 6 characters long");
      return false;
    }
    return true;
  };

  const signUp = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setLogoutMsg("");
    if (!validateSignup()) return;

    try {
      const cred = await createUserWithEmailAndPassword(
        auth,
        loginData.email,
        loginData.password
      );
      const firebaseToken = await getIdToken(cred.user);
      await sendTokenToAPI(firebaseToken);
    } catch (err) {
      setErrorMsg("Signup failed: " + err.message);
    }
  };

  const logIn = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setLogoutMsg("");

    try {
      const cred = await signInWithEmailAndPassword(
        auth,
        loginData.email,
        loginData.password
      );
      const firebaseToken = await getIdToken(cred.user);
      await sendTokenToAPI(firebaseToken, loginData.email);
    } catch (err) {
      setErrorMsg("Login failed: " + err.message);
    }
  };

  const sendTokenToAPI = async (firebaseToken, emailAddress) => {
    try {
      const { data } = await axios.post(
        `${configData.LOGIN_USER}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: firebaseToken,
          },
        }
      );

      const apiToken = data.token;
      const decoded = jwtDecode(apiToken);

      // Verify status
      let email = emailAddress || '';
      let userId = '';

      if (!email) {
        if (decoded.email && decoded.email.includes('@')) {
          email = decoded.email;
        } else if (decoded.sub && decoded.sub.includes('@')) {
          email = decoded.sub;
        } else {
          userId = decoded.sub;
        }
      }

      let userRes;
      if (email) {
        const queryEmail = email.trim().replace(/@/g, '%40');
        userRes = await axios.get(
          `${configData.GET_USER_BY_EMAIL_WITH_PARAM}/${queryEmail}`,
          {
            headers: {
              accept: 'application/hal+json',
              Authorization: apiToken,
            },
          }
        );
      } else if (userId) {
        userRes = await axios.get(
          `${configData.GET_USER_DETAILS}/${userId}`,
          {
            headers: {
              accept: 'application/hal+json',
              Authorization: apiToken,
            },
          }
        );
      }

      const userData = userRes?.data?.user;
      if (userData?.userStatus === 'INACTIVE' || userData?.userStatus === 'inactive') {
        setErrorMsg("This account has been deactivated. Please contact your administrator.");
        auth.signOut();
        localStorage.removeItem("token");
        localStorage.removeItem("tokenExpiry");
        return;
      }

      const expiry = decoded.exp * 1000;

      safeSetItem("token", apiToken);
      safeSetItem("tokenExpiry", expiry.toString());

      setToken(apiToken);
      recordUserSession(userData || { email, userId, orgId: decoded.orgId }, 'Dashboard / Logged In');
      logAnalyticsEvent('login', {
        method: 'email',
        user_role: (userData?.userType || 'USER').toUpperCase()
      });
      scheduleLogout(expiry);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.status === 401 ? "Unauthorized. Please check credentials." : "Login failed. User may not be registered.");
      auth.signOut();
      safeRemoveItem("token");
      safeRemoveItem("tokenExpiry");
    }
  };

  const signInWithGoogle = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    try {
      const result = await signInWithPopup(auth, gprovider);
      const firebaseToken = await getIdToken(result.user);
      await sendTokenToAPI(firebaseToken, result.user.email);
    } catch (err) {
      setErrorMsg("Google login failed: " + err.message);
    }
  };

  /* ---------- Password-reset handlers ---------- */
  const openResetUI = () => {
    setShowReset(true);
    setResetEmail(loginData.email);   // pre-fill with current login email
    setResetMsg("");
    setResetError("");
    setErrorMsg("");
    setLogoutMsg("");
    setResetStep(1);
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleResetEmailSubmit = async (e) => {
    e.preventDefault();
    setResetMsg("");
    setResetError("");

    if (!resetEmail) {
      setResetError("Please enter your email address.");
      return;
    }

    setResetMsg("Processing... Please wait.");
    try {
      const { deleteUserFromFirebaseBestEffort } = await import('../firebaseUtils');
      await deleteUserFromFirebaseBestEffort(resetEmail);
      setResetMsg("");
      setResetStep(2);
    } catch (err) {
      console.warn("Firebase delete failed, proceeding to signup anyway:", err);
      setResetMsg("");
      setResetStep(2);
    }
  };

  const handleResetSignupSubmit = async (e) => {
    e.preventDefault();
    setResetMsg("");
    setResetError("");

    if (!newPassword || !confirmPassword) {
      setResetError("Please enter password and confirm password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError("Passwords do not match.");
      return;
    }

    if (newPassword.length < 6) {
      setResetError("Password must be at least 6 characters long.");
      return;
    }

    setResetMsg("Updating password...");
    try {
      const cred = await createUserWithEmailAndPassword(auth, resetEmail, newPassword);
      const firebaseToken = await getIdToken(cred.user);
      await sendTokenToAPI(firebaseToken, resetEmail);
      toast.success("Password reset and logged in successfully!");
      closeResetUI();
    } catch (err) {
      setResetError("Failed to update password: " + err.message);
      setResetMsg("");
    }
  };

  const closeResetUI = () => {
    setShowReset(false);
    setResetEmail("");
    setResetMsg("");
    setResetError("");
    setResetStep(1);
    setNewPassword("");
    setConfirmPassword("");
  };

  const toggleMode = () => {
    setIsSignupMode((prev) => !prev);
    setLoginData({ email: "", password: "" });
    setErrorMsg("");
    setLogoutMsg("");
    setShowReset(false);
  };

  return (
    <div className="loginForm">
      <div className="boxLogin">
        <form className="menuLogin" onSubmit={(e) => e.preventDefault()}>
          <div className="titleLogin">{isSignupMode ? "Sign Up" : "Login"}</div>

          {/* ---------- Email ---------- */}
          <div className="unameLogin">
            <label className="label">Email</label>
            <input
              className="input"
              type="email"
              name="email"
              value={loginData.email}
              onChange={changeLogin}
              placeholder="you@example.com"
            />
          </div>

          {/* ---------- Password ---------- */}
          <div className="pwdLogin">
            <label className="label">Password</label>
            <div className="password-input-container">
              <input
                className="input"
                type={showPassword ? "text" : "password"}
                name="password"
                value={loginData.password}
                onChange={changeLogin}
                placeholder="••••••••"
              />
              <button
                type="button"
                className="toggle-pwd"
                onClick={() => setShowPassword((p) => !p)}
                tabIndex="-1"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {/* ---------- Submit button ---------- */}
          <div className="btnLogin">
            <button type="button" onClick={isSignupMode ? signUp : logIn}>
              {isSignupMode ? "Sign Up" : "Login"}
            </button>
          </div>

          {/* ---------- Forgot password link ---------- */}
          {!isSignupMode && !showReset && (
            <div className="forgot-row">
  <button type="button" className="forgot-link" onClick={openResetUI}>
    Forgot password?
  </button>
</div>
          )}

          {/* ---------- Global messages ---------- */}
          {logoutMsg && <div className="logoutMessage">{logoutMsg}</div>}
          {errorMsg && <div className="loginError">{errorMsg}</div>}
        </form>
      </div>

      {/* ==============================================================
          PASSWORD RESET OVERLAY (styled nicely)
      ============================================================== */}
      {showReset && (
        <div className="resetOverlay">
          <div className="resetCard">
            <h3 className="resetTitle">Reset Password</h3>
            <p className="resetDesc">
              {resetStep === 1
                ? "Enter your email address to continue."
                : `Set a new password for ${resetEmail}.`}
            </p>

            <form onSubmit={resetStep === 1 ? handleResetEmailSubmit : handleResetSignupSubmit} className="resetForm">
              {resetStep === 1 ? (
                <input
                  type="email"
                  className="resetInput"
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              ) : (
                <>
                  <input
                    type="password"
                    className="resetInput"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    style={{ marginBottom: "12px" }}
                  />
                  <input
                    type="password"
                    className="resetInput"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </>
              )}

              <div className="resetBtnGroup">
                <button type="submit" className="resetBtn primary">
                  {resetStep === 1 ? "Continue" : "Reset"}
                </button>
                <button
                  type="button"
                  className="resetBtn secondary"
                  onClick={closeResetUI}
                >
                  Cancel
                </button>
              </div>
            </form>

            {resetMsg && <div className="resetSuccess">{resetMsg}</div>}
            {resetError && <div className="resetError">{resetError}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;